import {useState, useEffect} from 'react'
import {useSearchParams} from 'react-router-dom'
import {Plus, Lock, Globe, Trash2, Settings2, Pencil, KanbanSquare} from 'lucide-react'
import {useApp} from '../context/AppContext'
import ViewToggle from '../components/board/ViewToggle'
import KanbanBoard from '../components/board/KanbanBoard'
import ListView from '../components/board/ListView'
import CalendarView from '../components/board/CalendarView'
import BoardForm from '../components/board/BoardForm'
import ColumnManager from '../components/board/ColumnManager'
import ExportMenu from '../components/board/ExportMenu'
import NewTaskForm from '../components/board/NewTaskForm'
import BoardFilters, {
  type BoardFilters as BoardFiltersType,
  defaultBoardFilters,
} from '../components/board/BoardFilters'
import ConfirmDialog from '../components/common/ConfirmDialog'
import Badge from '../components/common/Badge'
import PageHeader from '../components/common/PageHeader'

import {isTaskOverdue} from '../utils/helpers'
import {matchesTaskFilters} from '../components/board/taskFilters'

import {PageTitle} from '../../../../_metronic/layout/core'

export default function BoardPage() {
  const {state, dispatch, getBoardById, apiDeleteBoard, getTasksForBoard} = useApp()
  const [searchParams, setSearchParams] = useSearchParams()
  const boardIdFromUrl = searchParams.get('boardId')

  const [showNewBoard, setShowNewBoard] = useState(false)
  const [showEditBoard, setShowEditBoard] = useState(false)
  const [showNewTask, setShowNewTask] = useState(false)
  const [showColumnManager, setShowColumnManager] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [filters, setFilters] = useState<BoardFiltersType>(defaultBoardFilters)

  useEffect(() => {
    if (boardIdFromUrl && state.boards.length > 0) {
      const boardExists = state.boards.find((b) => b.id === boardIdFromUrl)

      if (boardExists && boardIdFromUrl !== state.currentBoardId) {
        dispatch({type: 'SET_BOARD', payload: boardIdFromUrl})
      }
    }
  }, [boardIdFromUrl, state.currentBoardId, state.boards, dispatch])

  useEffect(() => {
    setFilters(defaultBoardFilters)
    setShowEditBoard(false)
    setShowNewTask(false)
    setShowColumnManager(false)
    setDeleteConfirm(false)
  }, [state.currentBoardId])

  useEffect(() => {
    if (!getBoardById(state.currentBoardId) && state.boards.length) {
      const nextBoard = state.boards.find((item) => item.id === boardIdFromUrl) || state.boards[0]
      dispatch({type: 'SET_BOARD', payload: nextBoard.id})
    }
  }, [state.currentBoardId, state.boards, boardIdFromUrl, dispatch, getBoardById])

  const board = getBoardById(state.currentBoardId)

  if (!board) {
    return (
      <div className='devops-page'>
        <div className='devops-empty-state gap-3'>
          <p className='text-sm text-slate-400'>Nenhum quadro selecionado</p>
          <button
            onClick={() => setShowNewBoard(true)}
            className='flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium text-white bg-brand-500 hover:bg-brand-600 transition-colors'
          >
            <Plus size={16} />
            Criar Quadro
          </button>
          {showNewBoard && <BoardForm onClose={() => setShowNewBoard(false)} />}
        </div>
      </div>
    )
  }

  const boardTasks = state.tasks.filter((task) => task.boardId === board.id)
  const completed = boardTasks.filter((task) => task.status === 'done').length
  const overdue = boardTasks.filter((task) => isTaskOverdue(task)).length
  const unassigned = boardTasks.filter((task) => !task.assigneeIds.length).length
  const visibleTasks = getTasksForBoard(board.id).filter((task) =>
    matchesTaskFilters(task, filters, state.currentUser?.id)
  )
  const allStatuses = Array.from(
    new Set([...board.columns.map((c) => c.status), ...boardTasks.map((task) => task.status)])
  )
  const hasFilters =
    JSON.stringify(filters) !== JSON.stringify(defaultBoardFilters) || Boolean(state.searchQuery)
  const clearFilters = () => {
    setFilters(defaultBoardFilters)
    dispatch({type: 'SET_SEARCH', payload: ''})
  }

  return (
    <div className='devops-page devops-page--board flex min-h-[620px] flex-col'>
      <PageTitle>DevOps - Quadro</PageTitle>
      <PageHeader
        eyebrow='Quadro de trabalho'
        icon={KanbanSquare}
        title={board.name}
        description={
          board.description || 'Organize o fluxo de trabalho e acompanhe cada etapa da entrega.'
        }
        meta={
          <Badge
            className={
              board.visibility === 'private'
                ? 'bg-amber-50 text-amber-700'
                : 'bg-slate-100 text-slate-600'
            }
          >
            {board.visibility === 'private' ? (
              <span className='flex items-center gap-1'>
                <Lock size={10} /> Privado
              </span>
            ) : (
              <span className='flex items-center gap-1'>
                <Globe size={10} /> Público
              </span>
            )}
          </Badge>
        }
        actions={
          <>
            <ViewToggle />
            <ExportMenu boardId={board.id} />
            <button
              onClick={() => setShowEditBoard(true)}
              className='devops-icon-button'
              aria-label='Editar quadro'
            >
              <Pencil size={15} />
            </button>
            <button
              onClick={() => setShowColumnManager(true)}
              className='devops-icon-button'
              aria-label='Gerenciar colunas'
            >
              <Settings2 size={16} />
            </button>
            <button onClick={() => setShowNewTask(true)} className='devops-primary-button'>
              <Plus size={15} />
              <span>Nova tarefa</span>
            </button>
            <button onClick={() => setShowNewBoard(true)} className='devops-secondary-button'>
              <Plus size={15} />
              <span>Novo quadro</span>
            </button>
            {state.boards.length >= 1 && (
              <button
                onClick={() => setDeleteConfirm(true)}
                className='devops-icon-button hover:!text-red-500 hover:!border-red-200'
                aria-label='Excluir quadro'
              >
                <Trash2 size={16} />
              </button>
            )}
          </>
        }
      />

      <div className='devops-surface mb-4 flex flex-wrap items-center justify-between gap-3 px-4 py-3'>
        <label className='flex min-w-0 items-center gap-3 text-sm font-medium text-slate-600'>
          Quadro
          <select
            aria-label='Selecionar quadro'
            value={board.id}
            onChange={(e) => {
              const params = new URLSearchParams(searchParams)
              params.set('boardId', e.target.value)
              setSearchParams(params)
              dispatch({type: 'SET_BOARD', payload: e.target.value})
            }}
            className='min-w-0 max-w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800'
          >
            {state.boards.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <div className='flex flex-wrap items-center gap-4 text-xs text-slate-500'>
          <span>
            <strong className='text-slate-800'>{boardTasks.length}</strong> tarefas
          </span>
          <span>
            <strong className='text-emerald-700'>{completed}</strong> concluídas
          </span>
          <button
            type='button'
            className='text-red-600 hover:underline'
            onClick={() => setFilters({...defaultBoardFilters, dueDateRange: 'overdue'})}
          >
            <strong>{overdue}</strong> atrasadas
          </button>
          <button
            type='button'
            className='text-amber-700 hover:underline'
            onClick={() => setFilters({...defaultBoardFilters, assignment: 'unassigned'})}
          >
            <strong>{unassigned}</strong> abertas sem responsável
          </button>
        </div>
      </div>

      <div className='mb-4'>
        <BoardFilters
          filters={filters}
          onChange={setFilters}
          allStatuses={allStatuses}
          statusLabels={Object.fromEntries(
            board.columns.map((column) => [column.status, column.title])
          )}
        />
      </div>

      <div className='mb-3 flex flex-wrap items-center justify-between gap-2 px-1'>
        <p className='text-xs text-slate-500' role='status'>
          {visibleTasks.length} de {boardTasks.length} tarefas exibidas
          {state.currentView === 'calendar' ? ' · calendário organizado por prazo' : ''}
        </p>
        {hasFilters && (
          <button
            type='button'
            onClick={clearFilters}
            className='text-xs font-medium text-brand-600 hover:underline'
          >
            Limpar todos os filtros
          </button>
        )}
      </div>
      {visibleTasks.length === 0 && (
        <div className='devops-empty-state mb-4 gap-2 p-6'>
          <KanbanSquare size={28} className='text-slate-400' />
          <h3 className='text-sm font-semibold text-slate-700'>
            {boardTasks.length
              ? 'Nenhuma tarefa corresponde aos filtros'
              : 'Seu quadro está pronto para começar'}
          </h3>
          <p className='text-xs text-slate-500'>
            {boardTasks.length
              ? 'Ajuste a busca, o prazo ou os responsáveis para encontrar o que procura.'
              : 'Crie a primeira tarefa e acompanhe sua evolução em cada etapa.'}
          </p>
          <button
            type='button'
            onClick={boardTasks.length ? clearFilters : () => setShowNewTask(true)}
            className='devops-secondary-button mt-2'
          >
            {boardTasks.length ? 'Limpar filtros' : 'Criar primeira tarefa'}
          </button>
        </div>
      )}
      <div className='flex-1 min-h-[420px] overflow-hidden'>
        {state.currentView === 'kanban' && <KanbanBoard board={board} filters={filters} />}
        {state.currentView === 'list' && <ListView board={board} filters={filters} />}
        {state.currentView === 'calendar' && <CalendarView board={board} filters={filters} />}
      </div>

      {showNewBoard && <BoardForm onClose={() => setShowNewBoard(false)} />}
      {showEditBoard && <BoardForm board={board} onClose={() => setShowEditBoard(false)} />}
      {showNewTask && (
        <NewTaskForm
          boardId={board.id}
          initialStatus={board.columns[0]?.status || 'backlog'}
          onClose={() => setShowNewTask(false)}
        />
      )}
      {showColumnManager && (
        <ColumnManager board={board} onClose={() => setShowColumnManager(false)} />
      )}

      <ConfirmDialog
        isOpen={deleteConfirm}
        title='Excluir quadro'
        message={`Tem certeza que deseja excluir o quadro "${board.name}"? Todas as tarefas associadas permanecerão no sistema.`}
        confirmLabel='Excluir'
        onConfirm={async () => {
          await apiDeleteBoard(board.id)
          setDeleteConfirm(false)
        }}
        onCancel={() => setDeleteConfirm(false)}
      />
    </div>
  )
}
